import Dropdown from "@components/Dropdown/Dropdown"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import SectionLabel from "@components/SectionLabel/SectionLabel"
import TextInput from "@components/TextInput/TextInput"
import { useState, useEffect, useRef } from "react"
import { FormContainer, FormTopSection, FormInputsContainer, BeneficiaryContainer, BeneficiaryLabel, BeneficiaryValue, FormButtonsContainer } from "./ManageBank.styles"
import { useTranslation } from "react-i18next"
import type { SupportedBank } from "@hooks/useSupportedBank"
import { useUserRestrictions } from "@hooks/useUserRestrictions"

const FOCUS_SCROLL_DELAY = 500;
const SCROLL_OFFSET_ACCOUNT_NUMBER = 200;
const SCROLL_OFFSET_NICKNAME = 300;

export const BankForm = ({
    onSave,
    onRemove,
    supportedBanks,
    initialName,
    initialAccountNumber,
    initialProvider,
    initialNickname,
    uuid,
    errorText,
    mode,
}: {
    onSave: (data: any) => void
    onRemove: (uuid: string) => void
    supportedBanks?: SupportedBank[]
    initialName?: string
    initialAccountNumber?: string
    initialProvider?: string
    initialNickname?: string
    uuid?: string
    errorText?: string
    mode: 'add' | 'edit'
}) => {
    const { t } = useTranslation();
    const tagInputRef = useRef<HTMLInputElement>(null);
    const name = initialName || ''
    const [accountNumber, setAccountNumber] = useState(initialAccountNumber || '')
    const [selectedBank, setSelectedBank] = useState<SupportedBank | null>(null)
    const [nickname, setNickname] = useState(initialNickname || '')
    const formContainerRef = useRef<HTMLDivElement>(null)
    const { canManageBankAccounts } = useUserRestrictions()

    useEffect(() => {
        if (initialProvider) {
            const bank = supportedBanks?.find(bank =>
                bank.display_name === initialProvider ||
                bank.name === initialProvider ||
                bank.code === initialProvider
            );
            if (bank) setSelectedBank(bank);
        }
    }, [initialProvider, supportedBanks]);

    const handleSaveClick = () => {
        onSave({
            bank_account: accountNumber,
            bank_name: selectedBank!.code,
            account_nickname: nickname,
            asset: "idr",
            source: "REDEMPTION",
            bank_uuid: uuid
        });
    };

    const handleRemoveClick = () => {
        onRemove(uuid!);
    };

    const handleAccountNumberFocus = () => {
        setTimeout(() => {
            if (formContainerRef.current) {
                const startPosition = formContainerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_ACCOUNT_NUMBER;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    formContainerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY)
    };

    const handleNicknameFocus = () => {
        setTimeout(() => {
            if (formContainerRef.current) {
                const startPosition = formContainerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_NICKNAME;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    formContainerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY)
    };

    return (
        <FormContainer>
            <FormTopSection ref={formContainerRef}>
                <SectionLabel>{t('bankAndAddress.bank.form.sectionLabel')}</SectionLabel>
                <FormInputsContainer>
                    <Dropdown
                        label={t('bankAndAddress.bank.form.bankSelector')}
                        placeholder={t('bankAndAddress.bank.form.bankPlaceholder')}
                        options={supportedBanks || []}
                        value={selectedBank}
                        onChange={setSelectedBank}
                        usePortal={true} // Use portal to render dropdown outside FormTopSection
                        disabled={!canManageBankAccounts()}
                    />
                    <TextInput
                        title={t('bankAndAddress.bank.form.accountNumber')}
                        placeholder={t('bankAndAddress.bank.form.accountNumberPlaceholder')}
                        value={accountNumber}
                        onChange={e => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
                        onFocus={handleAccountNumberFocus}
                        inputMode="numeric"
                        errorText={errorText}
                        isError={!!errorText}
                        disabled={!canManageBankAccounts()}
                    />
                    {(canManageBankAccounts() || nickname) && <TextInput
                        title={t('bankAndAddress.bank.form.tag')}
                        isOptional
                        placeholder={t('bankAndAddress.bank.form.tagPlaceholder')}
                        value={nickname}
                        onChange={e => setNickname(e.target.value)}
                        onFocus={handleNicknameFocus}
                        ref={tagInputRef}
                        disabled={!canManageBankAccounts()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                tagInputRef.current?.blur();
                            }
                        }}
                    />}
                    {name && (
                        <BeneficiaryContainer>
                            <BeneficiaryLabel>{t('bankAndAddress.bank.form.beneficiaryLabel')}</BeneficiaryLabel>
                            <BeneficiaryValue>{name}</BeneficiaryValue>
                        </BeneficiaryContainer>
                    )}
                </FormInputsContainer>
            </FormTopSection>
            {canManageBankAccounts() && <FormButtonsContainer>
                {mode === 'edit' && <PrimaryButton onClick={handleRemoveClick} variant="danger">{t('bankAndAddress.bank.form.removeButton')}</PrimaryButton>}
                <PrimaryButton
                    onClick={handleSaveClick}
                    disabled={
                        !selectedBank ||
                        !accountNumber
                    }
                >
                    {t('bankAndAddress.bank.form.saveButton')}
                </PrimaryButton>
            </FormButtonsContainer>}
        </FormContainer>
    )
}